// src/Lexcode/components/GestureMouseControl.jsx
import React, { useRef, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import * as handpose from "@tensorflow-models/handpose";
import "@tensorflow/tfjs";
import "./GestureMouseControl.css";

export default function GestureMouseControl() {
  const videoRef = useRef();
  const canvasRef = useRef();
  const modelRef = useRef();
  const dimsRef = useRef({ vw: 0, vh: 0 });
  const lastClick = useRef(0);
  const hoverRef = useRef(null);

  const [cursor, setCursor] = useState({ x: -100, y: -100, clicking: false });
  const [pinching, setPinching] = useState(false);

  const FPS = 30;
  const SMOOTHING = 0.2;
  let stream, intervalId;

  // thresholds & state
  const PINCH_THRESH = 40;
  const FIST_THRESH = 60;
  const SCROLL_SPEED = 10;

  let wasIndexPinched = false;
  let wasPinkyPinched = false;
  let dragging = false;
  let prevPalmY = null;

  function synthEvent(type, x, y) {
    const el = document.elementFromPoint(x, y);
    if (!el) return;
    const ev = new MouseEvent(type, {
      bubbles: true,
      cancelable: true,
      clientX: x,
      clientY: y,
    });
    el.dispatchEvent(ev);
  }

  useEffect(() => {
    async function setup() {
      stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = videoRef.current;
      video.srcObject = stream;
      await new Promise(r => (video.onloadedmetadata = r));
      video.play();

      dimsRef.current.vw = video.videoWidth;
      dimsRef.current.vh = video.videoHeight;

      const canvas = canvasRef.current;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      modelRef.current = await handpose.load();
      intervalId = setInterval(renderFrame, 1000 / FPS);
    }

    async function renderFrame() {
      const video = videoRef.current;
      const model = modelRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (!video.videoWidth || !model) return;

      // 1) detect hands (mirrored)
      const preds = await model.estimateHands(video, { flipHorizontal: true });

      // 2) draw hand + skeleton
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const bones = [
        [0,1],[1,2],[2,3],[3,4],
        [0,5],[5,6],[6,7],[7,8],
        [0,9],[9,10],[10,11],[11,12],
        [0,13],[13,14],[14,15],[15,16],
        [0,17],[17,18],[18,19],[19,20],
      ];
      const SKIN = "rgba(226, 198, 186, 0.6)"; 
      const EDGE = "rgba(225, 207, 182, 0.9)"; 
      preds.forEach(p => {
        const lm = p.landmarks.map(([x,y]) => [
          (x / dimsRef.current.vw) * canvas.width,
          (y / dimsRef.current.vh) * canvas.height,
        ]);

        // palm fill
        ctx.save();
        ctx.fillStyle = SKIN;
        ctx.beginPath();
        [0,5,9,13,17].forEach((i, idx) => {
          const [X, Y] = lm[i];
          idx === 0 ? ctx.moveTo(X, Y) : ctx.lineTo(X, Y);
        });
        ctx.closePath();
        ctx.fill();
        ctx.restore();

        // finger fill
        ctx.save();
        ctx.fillStyle = SKIN;
        ctx.globalAlpha = 0.4;
        [[0,1,2,3,4],[0,5,6,7,8],[0,9,10,11,12],[0,13,14,15,16],[0,17,18,19,20]]
          .forEach(seg => {
            ctx.beginPath();
            seg.forEach((i, idx) => {
              const [X, Y] = lm[i];
              idx === 0 ? ctx.moveTo(X, Y) : ctx.lineTo(X, Y);
            });
            ctx.closePath();
            ctx.fill();
          });
        ctx.restore();

        // joints + bones
        ctx.strokeStyle = EDGE;
        ctx.lineWidth = 2;
        lm.forEach(([X, Y]) => {
          ctx.beginPath();
          ctx.arc(X, Y, 4, 0, 2 * Math.PI);
          ctx.fillStyle = EDGE;
          ctx.fill();
        });
        bones.forEach(([i, j]) => {
          const [X1, Y1] = lm[i];
          const [X2, Y2] = lm[j];
          ctx.beginPath();
          ctx.moveTo(X1, Y1);
          ctx.lineTo(X2, Y2);
          ctx.stroke();
        });
      });

      // no hand → reset
      if (!preds.length) {
        wasIndexPinched = wasPinkyPinched = dragging = false;
        prevPalmY = null;
        setPinching(false);
        hoverRef.current = null;
        return;
      }

      // key landmarks
      const [ix, iy] = preds[0].landmarks[8];
      const [tx, ty] = preds[0].landmarks[4];
      const [px, py] = preds[0].landmarks[20];
      const palm = preds[0].landmarks[0];

      // screen coords
      const cx = window.innerWidth  * (1 - ix / dimsRef.current.vw);
      const cy = window.innerHeight * ( iy / dimsRef.current.vh);

      // smooth cursor
      setCursor(c => ({
        x: c.x + (cx - c.x) * SMOOTHING,
        y: c.y + (cy - c.y) * SMOOTHING,
        clicking: c.clicking,
      }));

      // hover detection
      const hoverEl = document.elementFromPoint(cx, cy);
      if (hoverEl) {
        const rect = hoverEl.getBoundingClientRect();
        hoverRef.current = {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
        };
      } else {
        hoverRef.current = null;
      }

      // pinch → click/dblclick
      const isIndexPinch = Math.hypot(ix - tx, iy - ty) < PINCH_THRESH;
      setPinching(isIndexPinch);
      if (isIndexPinch && !wasIndexPinched) {
        const now = Date.now();
        synthEvent("mousedown", cx, cy);
        synthEvent("mouseup",   cx, cy);
        synthEvent("click",     cx, cy);
        if (lastClick.current && now - lastClick.current < 300) {
          synthEvent("dblclick", cx, cy);
        }
        lastClick.current = now;
      }
      wasIndexPinched = isIndexPinch;

      // pinky pinch → right-click
      const isPinkyPinch = Math.hypot(px - tx, py - ty) < PINCH_THRESH;
      if (isPinkyPinch && !wasPinkyPinched) {
        synthEvent("contextmenu", cx, cy);
      }
      wasPinkyPinched = isPinkyPinch;

      // drag & drop
      if (isIndexPinch) {
        if (!dragging) {
          dragging = true;
          synthEvent("mousedown", cx, cy);
        }
        synthEvent("mousemove", cx, cy);
      } else if (dragging) {
        dragging = false;
        synthEvent("mouseup", cx, cy);
      }

      // fist → scroll
      const isFist = [4,8,12,16,20].every(i => {
        const [x, y] = preds[0].landmarks[i];
        return Math.hypot(x - palm[0], y - palm[1]) < FIST_THRESH;
      });
      if (isFist) {
        const screenPalmY = window.innerHeight * (palm[1] / dimsRef.current.vh);
        if (prevPalmY != null) {
          const dy = prevPalmY - screenPalmY;
          const target = document.scrollingElement || document.body;
          const wheel = new WheelEvent("wheel", {
            deltaY: -dy * SCROLL_SPEED,
            bubbles: true,
            cancelable: true
          });
          target.dispatchEvent(wheel);
        }
        prevPalmY = screenPalmY;
      } else {
        prevPalmY = null;
      }
    }

    setup();
    return () => {
      clearInterval(intervalId);
      stream?.getTracks().forEach(t => t.stop());
    };
  }, []);

  return ReactDOM.createPortal(
    <>
      {/* HOVER HIGHLIGHT */}
      {hoverRef.current && (
        <div
          style={{
            position:      "fixed",
            left:          hoverRef.current.x,
            top:           hoverRef.current.y,
            width:         hoverRef.current.width,
            height:        hoverRef.current.height,
            border:        "2px solidrgba(255, 255, 255, 0)",
            borderRadius:  4,
            pointerEvents: "none",
            zIndex:        99999998,
            boxShadow:     "0 0 6px rgba(228, 207, 198, 0.6)"
          }}
        />
      )}

      {/* ORANGE DOT CURSOR */}
      <div
        className="gesture-dot"
        style={{
          position:      "fixed",
          top:           cursor.y - 6,
          left:          cursor.x - 6,
          width:         12,
          height:        12,
          background:    "tomato",
          borderRadius:  "50%",
          pointerEvents: "none",
          zIndex:        99999999,
          transition:    "transform 0.05s ease-out"
        }}
      />

      {/* VIDEO & CANVAS */}
      <video
        ref={videoRef}
        style={{
          position:      "fixed",
          top:           0,
          left:          0,
          width:         "100%",
          height:        "100%",
          transform:     "scaleX(-1)",
          opacity:       0,
          pointerEvents: "none",
        }}
        muted
        playsInline
      />
      <canvas
        ref={canvasRef}
        className="gesture-canvas"
        style={{ position: "fixed", top: 0, left: 0, zIndex: 99999997 }}
      />

      {/* OVERLAY CURSOR & FEEDBACK */}
      <div
        className={`gesture-cursor ${cursor.clicking ? "clicking" : ""}`}
        style={{
          position: "fixed",
          top:      0,
          left:     0,
          zIndex:   99999996,
          transform:`translate(${cursor.x}px, ${cursor.y}px)`
        }}
      />
      <div className="gesture-feedback" style={{ zIndex: 99999999 }}>
        {pinching
          ? "🤏 (Thumb + Index) Pinch to Click!"
          : "✊ Make a Fist to Scroll"}
      </div>
    </>,
    document.getElementById("gesture-overlay-root")
  );
}
